import "../helpers/playerjs/player-0.1.0.min.js";
import "../helpers/tracking_loader";

// Detect iOS standalone (Add to Home Screen) mode for CSS targeting.
// iOS uses navigator.standalone instead of the W3C display-mode media query.
if (window.navigator.standalone) {
  document.documentElement.classList.add("pwa-standalone");
}

import { Application } from "@hotwired/stimulus";
import "@hotwired/turbo-rails";
import * as ActiveStorage from "@rails/activestorage";
import { installCsrfTokenRefresh } from "../helpers/csrf";
import { registerTurboStreamActions } from "../helpers/turbo_stream_actions";

const application = Application.start();

// Configure Stimulus development experience
application.debug = false;
window.Stimulus = application;

registerTurboStreamActions();
installCsrfTokenRefresh();

// Scroll to URL hash anchor or scroll_to parameter after Turbo navigation
// (Turbo Drive doesn't automatically scroll to anchors after redirects)
document.addEventListener("turbo:load", () => {
  let targetId = null;

  // Check for scroll_to query parameter (survives redirects better than URL hash)
  const urlParams = new URLSearchParams(window.location.search);
  const scrollToParam = urlParams.get("scroll_to");
  if (scrollToParam) {
    targetId = scrollToParam;
  }

  // Also check for URL hash as fallback
  if (!targetId && window.location.hash) {
    targetId = window.location.hash.substring(1); // Remove the # prefix
  }

  if (targetId) {
    const element = document.getElementById(targetId);
    if (element) {
      requestAnimationFrame(() => {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }
});

ActiveStorage.start();

// Remove unread indicators for viewed posts.
// On restore (history.back), Turbo renders the cached page then fires before-cache again.
// We clean up in both events: render (so user sees clean page) and before-cache (so future restores are clean).
function removeViewedPostIndicators() {
  const viewed = JSON.parse(sessionStorage.getItem("viewedPostIds") || "[]");
  if (!viewed.length) return;

  viewed.forEach((frameId) => {
    const frame = document.getElementById(frameId);
    if (!frame) return;
    frame.querySelectorAll("[class*='--unread']").forEach((el) => {
      el.classList.forEach((cls) => {
        if (cls.includes("--unread")) el.classList.remove(cls);
      });
    });
  });
}

document.addEventListener("turbo:render", removeViewedPostIndicators);
document.addEventListener("turbo:before-cache", removeViewedPostIndicators);

// Generic controllers
const genericControllers = import.meta.glob(
  "./../controllers/**/*_controller.js",
  { eager: true },
);

for (let path in genericControllers) {
  let module = genericControllers[path];
  let name = path
    .match(/.*controllers\/(?:[^\/]+\/)*([^\/]+)_controller\.js$/)[1]
    .replaceAll("_", "-");

  if (!module.default) {
    throw new Error(`No default export found for ${path}.`);
  }
  application.register(name, module.default);
}

// Component JS (SCSS import, JS and Stimulus)
const componentControllers = import.meta.glob(
  "./../components/**/component.js",
  {
    eager: true,
  },
);

for (let path in componentControllers) {
  let module = componentControllers[path];
  let name = path
    .match(/components\/(.+)\/component\.js$/)[1]
    .replaceAll("/", "-")
    .replaceAll("_", "-");

  if (!module.default) {
    throw new Error(`No default export found for ${path}.`);
  }

  application.register(name, module.default);
}

export default application;
